import { useState } from 'react';
import { Check, ChevronDown, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface StatusOption {
  value: number;
  label: string;
  badgeClass: string;
}

const statusOptions: StatusOption[] = [
  { value: 1, label: 'Running', badgeClass: 'bg-yellow-100 text-yellow-800' },
  { value: 2, label: 'Completed', badgeClass: 'bg-green-100 text-green-800' },
  { value: 3, label: 'Failed', badgeClass: 'bg-red-100 text-red-800' },
];

interface StatusMultiSelectProps {
  selectedStatuses: number[];
  onSelectionChange: (statuses: number[]) => void;
}

export const StatusMultiSelect = ({
  selectedStatuses,
  onSelectionChange,
}: StatusMultiSelectProps) => {
  const [open, setOpen] = useState(false);

  const handleStatusToggle = (statusValue: number) => {
    const isSelected = selectedStatuses.includes(statusValue);
    if (isSelected) {
      onSelectionChange(selectedStatuses.filter((s) => s !== statusValue));
    } else {
      onSelectionChange([...selectedStatuses, statusValue]);
    }
  };

  const getSelectedLabels = () => {
    if (selectedStatuses.length === 0) return 'All statuses';
    if (selectedStatuses.length === statusOptions.length) return 'All statuses';
    return selectedStatuses
      .map((status) => statusOptions.find((opt) => opt.value === status)?.label)
      .filter(Boolean)
      .join(', ');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="justify-between min-w-[150px]">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            <span className="truncate">{getSelectedLabels()}</span>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Filter statuses..." />
          <CommandList>
            <CommandEmpty>No statuses found.</CommandEmpty>
            <CommandGroup>
              {statusOptions.map((option) => {
                const isSelected = selectedStatuses.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => handleStatusToggle(option.value)}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-4 h-4">
                        {isSelected && <Check className="h-4 w-4" />}
                      </div>
                      <Badge variant="secondary" className={option.badgeClass}>
                        {option.label}
                      </Badge>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
